/**
 * Wally AI Search Override
 *
 * Intercepts the predictive search component and replaces Shopify's native
 * search with results from the Wally search Worker (Voyage AI + Claude Haiku).
 * Falls back to Shopify native search if the Worker is unreachable.
 */

const WORKER_TIMEOUT = 2000;

function formatPrice(price) {
  return `$${Number(price).toFixed(2).replace(/\.00$/, '')}`;
}

function buildPriceHtml(product) {
  if (!product.available) {
    return '<span class="price__sold-out">Sold out</span>';
  }
  if (product.compareAtPrice && product.compareAtPrice > product.price) {
    return `<span class="compare-at-price">${formatPrice(product.compareAtPrice)}</span> <span class="price__sale">${formatPrice(product.price)}</span>`;
  }
  return `<span>${formatPrice(product.price)}</span>`;
}

function buildResultsHtml(results, message) {
  if (!results.length) {
    return `<div id="predictive-search-results" class="predictive-search-dropdown" role="listbox">
      <div class="predictive-search-results__inner" data-search-results>
        <div class="predictive-search-results__no-results">
          <p>No results found</p>
        </div>
      </div>
    </div>`;
  }

  const messageHtml = message
    ? `<div class="predictive-search-results__message" style="padding: 12px 20px 4px; font-size: 14px; color: #666;"><p style="margin: 0;">${escapeHtml(message)}</p></div>`
    : '';

  const cards = results.map((p, i) => {
    const delay = Math.min(i * 30, 150);
    const imgSrc = p.imageUrl ? p.imageUrl.replace(/\?.*/, '') + '?width=352&height=440&crop=center' : '';
    return `<li class="predictive-search-results__card predictive-search-results__card--product" ref="resultsItems[]" style="animation-delay: ${delay}ms">
      <div class="resource-card" data-resource-type="product">
        <a class="resource-card__link" href="${p.url}">
          <span class="visually-hidden">${escapeHtml(p.title)}</span>
        </a>
        <div class="resource-card__media" style="--resource-card-aspect-ratio: 0.8;">
          ${imgSrc ? `<img class="resource-card__image" src="${imgSrc}" alt="${escapeHtml(p.title)}" loading="lazy" width="352" height="440" />` : ''}
        </div>
        <div class="resource-card__content">
          <p class="resource-card__title paragraph">${escapeHtml(p.title)}</p>
          <p class="resource-card__vendor caption">${escapeHtml(p.vendor)}</p>
          <div class="price">${buildPriceHtml(p)}</div>
        </div>
      </div>
    </li>`;
  }).join('\n');

  return `<div id="predictive-search-results" class="predictive-search-dropdown" role="listbox">
    <div class="predictive-search-results__inner" data-search-results>
      ${messageHtml}
      <div id="predictive-search-products" class="predictive-search-results__products">
        <ul class="predictive-search-results__list predictive-search-results__wrapper-products list-unstyled" role="listbox" aria-label="Products">
          ${cards}
        </ul>
      </div>
    </div>
  </div>`;
}

function escapeHtml(str) {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

async function fetchWorkerResults(workerUrl, query, limit = 8) {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), WORKER_TIMEOUT);

  try {
    const res = await fetch(`${workerUrl}/search`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query, limit }),
      signal: controller.signal,
    });
    clearTimeout(timeout);
    if (!res.ok) return null;
    return await res.json();
  } catch {
    clearTimeout(timeout);
    return null;
  }
}

function debounce(fn, delay) {
  let timer;
  return function (...args) {
    clearTimeout(timer);
    timer = setTimeout(() => fn.apply(this, args), delay);
  };
}

function initWallySearch() {
  const component = document.querySelector('predictive-search-component');
  if (!component) return;

  const workerUrl = component.dataset.wallySearchUrl;
  if (!workerUrl) return;

  // Store the original search method for fallback
  const originalSearch = component.search;

  // Override the search method (debounced to match original 200ms)
  component.search = debounce(function (event) {
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();

    if (!searchTerm.length) {
      originalSearch.call(this, event);
      return;
    }

    // Show reset button
    if (this.refs.resetButton) {
      this.refs.resetButton.hidden = false;
    }

    // Call Worker API
    fetchWorkerResults(workerUrl, searchTerm).then((data) => {
      // Check input hasn't changed while we were fetching
      if (this.refs.searchInput.value.trim() !== searchTerm) return;

      if (!data || !data.results) {
        originalSearch.call(this, event);
        return;
      }

      const html = buildResultsHtml(data.results, data.message);
      const container = this.refs.predictiveSearchResults;
      container.innerHTML = html;

      // Reset scroll
      requestAnimationFrame(() => {
        container.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
        this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
      });
    });
  }, 200);

  // Override Enter key to navigate to custom search page instead of /search
  const originalKeyDown = component.onSearchKeyDown;
  component.onSearchKeyDown = function (event) {
    if (event.key === 'Enter') {
      // Check if a result item is selected (keyboard nav)
      const selected = this.querySelector('[aria-selected="true"] a');
      if (selected) {
        event.preventDefault();
        selected.click();
        return;
      }

      // No item selected: go to custom search page
      const query = this.refs.searchInput.value.trim();
      if (query) {
        event.preventDefault();
        window.location.href = `/pages/search?q=${encodeURIComponent(query)}`;
        return;
      }
    }

    // All other keys: use original handler
    originalKeyDown.call(this, event);
  };

  // Override View All button to go to custom search page
  const viewAllBtn = component.querySelector('.predictive-search__search-button');
  if (viewAllBtn) {
    viewAllBtn.addEventListener('click', (e) => {
      e.preventDefault();
      const query = component.refs.searchInput.value.trim();
      if (query) {
        window.location.href = `/pages/search?q=${encodeURIComponent(query)}`;
      }
    });
  }
}

// Wait for the custom element to be defined and upgraded, then override
customElements.whenDefined('predictive-search-component').then(() => {
  // Small delay to ensure connectedCallback has run and refs are set up
  setTimeout(initWallySearch, 100);
});
